import { useGetFeaturesAPI } from "@/api/features/useFeaturesAPI";
import { useAppFeatures } from "@/hooks/useAppFeatures";

import {
  EModuleName,
  EModulePermission,
  useGetUserPermission
} from "./useGetUserPermission";

// `/features` only returns display names; change here once BE exposes a stable key
const MODULE_FEATURE_NAME: Partial<Record<EModuleName, string>> = {
  [EModuleName.USER_MANAGEMENT]: "User Management",
  [EModuleName.DATASET_MANAGEMENT]: "Dataset Management"
};

/**
 * Tells if a module is in the organization's plan, based on `/features`.
 * Fails open (in plan) when the flag is off, data is missing, or the module is unmapped.
 */
export const useIsModuleInPlan = (
  moduleName: EModuleName,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  const { isGranularRbacEnabled } = useAppFeatures();
  const { data, isLoading } = useGetFeaturesAPI(null, {
    enabled: enabled && isGranularRbacEnabled
  });

  const featureName = MODULE_FEATURE_NAME[moduleName];
  const isInPlan =
    !isGranularRbacEnabled ||
    !featureName ||
    !data ||
    data.data.some(({ name }) => name === featureName);

  return { isInPlan, isLoading };
};

/**
 * With granular RBAC, a module missing from the plan stays in the menu
 * so the user can open its Contact Sales modal.
 */
export const useIsModuleMenuVisible = (moduleName: EModuleName) => {
  const { isGranularRbacEnabled } = useAppFeatures();
  const userPermissions = useGetUserPermission();
  const { isInPlan } = useIsModuleInPlan(moduleName);
  const isViewGranted = userPermissions[moduleName][EModulePermission.VIEW];

  const isVisible = isGranularRbacEnabled
    ? !isInPlan || isViewGranted
    : isViewGranted;

  return { isVisible, isInPlan };
};
